import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { usePagination } from "@/hooks/usePagination";
import { throwIfNotOk } from "@/lib/api-error";
import {
  TherapiesFilterBar,
  TherapiesTable,
  TherapiesLoadingSkeleton,
  TherapiesEmptyState,
  TherapiesStats,
  toLocalISODate,
  type TherapyFilters,
  ITEMS_PER_PAGE,
} from "@/components/admin/therapies";
import type { PaginatedTherapies } from "@/types";

export const Route = createFileRoute("/dashboard/admin/therapies")({
  component: AdminTherapies,
});

function AdminTherapies() {
  const {
    page,
    setPage,
    searchQuery: therapistSearch,
    debouncedSearch: debouncedTherapist,
    setSearch: handleTherapistChange,
    filters,
    setFilter,
    setFilters,
    resetAll,
    pageSize,
  } = usePagination<TherapyFilters>(
    { pageSize: ITEMS_PER_PAGE },
    { dateFrom: undefined, dateTo: undefined, status: "", paymentStatus: "", payoutStatus: "" },
  );

  const { dateFrom, dateTo, status, paymentStatus, payoutStatus } = filters;

  const dateFromISO = dateFrom ? toLocalISODate(dateFrom) : "";
  const dateToISO = dateTo ? toLocalISODate(dateTo) : "";

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (dateFrom || dateTo) count++;
    if (status) count++;
    if (paymentStatus) count++;
    if (payoutStatus) count++;
    if (debouncedTherapist) count++;
    return count;
  }, [dateFrom, dateTo, status, paymentStatus, payoutStatus, debouncedTherapist]);

  const { data, isLoading } = useQuery({
    queryKey: [
      "admin",
      "therapies",
      page,
      dateFromISO,
      dateToISO,
      status,
      paymentStatus,
      payoutStatus,
      debouncedTherapist,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
      });
      if (dateFromISO) params.set("dateFrom", dateFromISO);
      if (dateToISO) params.set("dateTo", dateToISO);
      if (status) params.set("status", status);
      if (paymentStatus) params.set("paymentStatus", paymentStatus);
      if (payoutStatus) params.set("payoutStatus", payoutStatus);
      if (debouncedTherapist) params.set("search", debouncedTherapist);
      const res = await fetch(`/api/admin/therapies?${params}`);
      await throwIfNotOk(res, "Greška pri učitavanju terapija");
      return res.json() as Promise<PaginatedTherapies>;
    },
    placeholderData: (prev) => prev,
  });

  const therapies = data?.data ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="mx-auto max-w-6xl space-y-5 pb-14 sm:space-y-6">
      {/* Header */}
      <header>
        <h1
          className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          Sve terapije
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Pregled svih terapijskih sesija na platformi.
        </p>
      </header>

      <TherapiesStats total={data?.total} summary={data?.summary} />

      <TherapiesFilterBar
        therapistSearch={therapistSearch}
        debouncedTherapist={debouncedTherapist}
        onSearchChange={handleTherapistChange}
        filters={filters}
        onFilterChange={setFilter}
        onFiltersChange={setFilters}
        onResetAll={resetAll}
      />

      {/* Content */}
      {isLoading && therapies.length === 0 ? (
        <TherapiesLoadingSkeleton />
      ) : therapies.length === 0 ? (
        <TherapiesEmptyState hasFilters={activeFilterCount > 0} onClear={resetAll} />
      ) : (
        <TherapiesTable
          therapies={therapies}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
